/* --------------------------------------------------------------------------
   Custom blog section — slider pagination, sorting, tabs, and video modals
   -------------------------------------------------------------------------- */

const MAX_VISIBLE_PAGES = 4;
const TABLET_DOWN_BLOG_MAX_WIDTH = 959;
const SECTION_SELECTOR = '[data-custom-blog-section]';

let articleVideoModalCleanupBound = false;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const getCustomBlogSection = (element) => element?.closest(SECTION_SELECTOR) ?? null;

const forEachSection = (root, callback) => {
    const scope = root === document ? document : root;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(callback);
};

const initOncePerSection = (root, datasetKey, callback) => {
    forEachSection(root, (sectionRoot) => {
        if (sectionRoot.dataset[datasetKey]) return;
        sectionRoot.dataset[datasetKey] = 'true';
        callback(sectionRoot);
    });
};

const isTabletDownBlogLayout = () =>
    window.matchMedia(`(max-width: ${TABLET_DOWN_BLOG_MAX_WIDTH}px)`).matches;

const getArticleWrapper = (post) => post.querySelector('.article-card-wrapper');

const getDefaultSort = (sectionRoot) => sectionRoot?.dataset.defaultSort || 'most-relevant';

const getMobilePickerType = (sectionRoot) => sectionRoot?.dataset.mobilePickerType || 'dropdown';

const usesMobileDropdownPicker = (sectionRoot) => getMobilePickerType(sectionRoot) === 'dropdown';

const getRelevantOrder = (sectionRoot) => {
    const order = sectionRoot?.dataset.relevantOrder;
    if (!order) return [];
    return order.split(',').map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));
};

// ---------------------------------------------------------------------------
// Slider pagination
// ---------------------------------------------------------------------------

const getPaginationItems = (current, total) => {
    if (total <= MAX_VISIBLE_PAGES) {
        return Array.from({ length: total }, (_, index) => ({ type: 'page', page: index + 1 }));
    }

    if (current <= 3) {
        return [
            { type: 'page', page: 1 },
            { type: 'page', page: 2 },
            { type: 'page', page: 3 },
            { type: 'ellipsis' },
            { type: 'page', page: total },
        ];
    }

    if (current >= total - 2) {
        return [
            { type: 'page', page: 1 },
            { type: 'ellipsis' },
            { type: 'page', page: total - 2 },
            { type: 'page', page: total - 1 },
            { type: 'page', page: total },
        ];
    }

    return [
        { type: 'page', page: 1 },
        { type: 'ellipsis' },
        { type: 'page', page: current },
        { type: 'ellipsis' },
        { type: 'page', page: total },
    ];
};

const renderPaginationNumbers = (container, current, total) => {
    container.innerHTML = '';

    getPaginationItems(current, total).forEach((item) => {
        if (item.type === 'ellipsis') {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'slider-pagination-ellipsis';
            ellipsis.setAttribute('aria-hidden', 'true');
            ellipsis.textContent = '...';
            container.appendChild(ellipsis);
            return;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'slider-counter__link slider-counter__link--numbers link';
        button.dataset.page = String(item.page);
        button.textContent = String(item.page);
        button.setAttribute('aria-label', `Load slide ${item.page} of ${total}`);

        if (item.page === current) {
            button.classList.add('slider-counter__link--active');
            button.setAttribute('aria-current', 'true');
        }

        container.appendChild(button);
    });
};

const getBlogSliderState = (sliderComponent) => {
    const slider = sliderComponent.querySelector('[id^="Slider-"]');
    if (!slider) return null;

    const pages = Array.from(slider.children)
        .filter((child) => child.id?.startsWith('Slide-') || child.classList.contains('slider-slide'))
        .filter((child) => child.clientWidth > 0);

    if (!pages.length) return null;

    const offset = pages.length > 1
        ? pages[1].offsetLeft - pages[0].offsetLeft
        : pages[0].clientWidth;

    return { slider, pages, offset, total: pages.length };
};

const getBlogSliderCurrentPage = (sliderComponent) => {
    const currentElement = sliderComponent.querySelector('.slider-counter--current');
    return currentElement ? parseInt(currentElement.textContent, 10) || 1 : 1;
};

const goToBlogSliderPage = (sliderComponent, page, state = getBlogSliderState(sliderComponent)) => {
    if (!state?.offset) return;

    const targetPage = Math.min(state.total, Math.max(1, page));
    state.slider.scrollTo({ left: (targetPage - 1) * state.offset, behavior: 'smooth' });

    const currentElement = sliderComponent.querySelector('.slider-counter--current');
    if (currentElement) currentElement.textContent = String(targetPage);

    sliderComponent.blogPaginationUpdate?.();
};

const initBlogSliderPagination = (root = document) => {
    const scope = root === document ? document : root;

    scope.querySelectorAll('slider-component').forEach((sliderComponent) => {
        const numbersContainer = sliderComponent.querySelector('.slider-pagination-numbers');
        const prevButton = sliderComponent.querySelector('button[name="previous"]');
        const nextButton = sliderComponent.querySelector('button[name="next"]');

        if (!numbersContainer || !prevButton || !nextButton) return;
        if (sliderComponent.dataset.paginationInit) return;

        sliderComponent.dataset.paginationInit = 'true';

        const getTotalPages = () => {
            const state = getBlogSliderState(sliderComponent);
            if (state) return state.total;

            const totalElement = sliderComponent.querySelector('.slider-counter--total');
            const fromCounter = totalElement ? parseInt(totalElement.textContent, 10) : 0;
            const fromData = parseInt(numbersContainer.dataset.totalPages, 10) || 0;
            return fromCounter || fromData;
        };

        const updatePagination = () => {
            const total = getTotalPages();
            if (total <= 1) return;
            renderPaginationNumbers(numbersContainer, getBlogSliderCurrentPage(sliderComponent), total);
        };

        sliderComponent.blogPaginationUpdate = updatePagination;
        updatePagination();

        const slider = sliderComponent.querySelector('[id^="Slider-"]');
        if (slider && !slider.dataset.blogPaginationScrollSync) {
            slider.dataset.blogPaginationScrollSync = 'true';
            let scrollFrame;

            slider.addEventListener('scroll', () => {
                cancelAnimationFrame(scrollFrame);
                scrollFrame = requestAnimationFrame(() => {
                    const state = getBlogSliderState(sliderComponent);
                    const currentElement = sliderComponent.querySelector('.slider-counter--current');
                    if (!state?.offset || !currentElement) return;

                    const page = Math.min(
                        state.total,
                        Math.max(1, Math.round(state.slider.scrollLeft / state.offset) + 1)
                    );

                    if (parseInt(currentElement.textContent, 10) === page) return;

                    currentElement.textContent = String(page);
                    updatePagination();
                });
            }, { passive: true });
        }

        numbersContainer.addEventListener('click', (event) => {
            const button = event.target.closest('[data-page]');
            if (!button) return;

            event.preventDefault();
            goToBlogSliderPage(sliderComponent, parseInt(button.dataset.page, 10));
        });

        const onPagerClick = (event) => {
            const delta = event.currentTarget.name === 'next' ? 1 : -1;
            const current = getBlogSliderCurrentPage(sliderComponent);
            const total = getTotalPages();

            if (current + delta < 1 || current + delta > total) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            goToBlogSliderPage(sliderComponent, current + delta);
        };

        prevButton.addEventListener('click', onPagerClick, true);
        nextButton.addEventListener('click', onPagerClick, true);
        sliderComponent.addEventListener('slideChanged', updatePagination);
    });
};

const resetBlogSliders = (panel) => {
    if (!panel) return;

    panel.querySelectorAll('slider-component.desktop').forEach((slider) => {
        const track = slider.querySelector('[id^="Slider-"]');
        if (track) track.scrollLeft = 0;

        slider.resetPages?.();
        slider.blogPaginationUpdate?.();
    });
};

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

const compareBlogPosts = (postA, postB, sortValue, relevantOrder) => {
    const wrapA = getArticleWrapper(postA);
    const wrapB = getArticleWrapper(postB);
    if (!wrapA || !wrapB) return 0;

    const pubA = parseInt(wrapA.dataset.articlePublished, 10) || 0;
    const pubB = parseInt(wrapB.dataset.articlePublished, 10) || 0;
    const titleA = wrapA.dataset.articleTitle || '';
    const titleB = wrapB.dataset.articleTitle || '';
    const idA = parseInt(wrapA.dataset.articleId, 10) || 0;
    const idB = parseInt(wrapB.dataset.articleId, 10) || 0;

    switch (sortValue) {
        case 'most-relevant': {
            const idxA = relevantOrder.indexOf(idA);
            const idxB = relevantOrder.indexOf(idB);
            const rankA = idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA;
            const rankB = idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB;
            if (rankA !== rankB) return rankA - rankB;
            return pubB - pubA;
        }
        case 'oldest':
            return pubA - pubB;
        case 'title-asc':
            return titleA.localeCompare(titleB);
        case 'title-desc':
            return titleB.localeCompare(titleA);
        case 'newest':
        default:
            return pubB - pubA;
    }
};

const getPanelPostList = (panel) => {
    const mobileList = panel.querySelector('.blog__posts--mobile-list');
    if (mobileList) {
        return {
            container: mobileList,
            posts: Array.from(mobileList.querySelectorAll(':scope > li.blog__post')),
        };
    }

    const mobileTrack = panel.querySelector('slider-component.mobile [id^="Slider-"]');
    if (!mobileTrack) return null;

    return {
        container: mobileTrack,
        posts: Array.from(mobileTrack.querySelectorAll(':scope > li.blog__post')),
    };
};

const rebuildDesktopSlides = (desktopSlider, sortedPosts, cardsPerSlide) => {
    const track = desktopSlider.querySelector('[id^="Slider-"]');
    if (!track) return;

    const postById = new Map(
        Array.from(desktopSlider.querySelectorAll('.blog__post'))
            .map((post) => {
                const articleId = getArticleWrapper(post)?.dataset.articleId;
                return articleId ? [articleId, post] : null;
            })
            .filter(Boolean)
    );

    const sortedDesktopPosts = sortedPosts
        .map((mobilePost) => postById.get(getArticleWrapper(mobilePost)?.dataset.articleId))
        .filter(Boolean);

    track.innerHTML = '';

    const sliderIdBase = track.id.replace(/^Slider-/, '');

    for (let index = 0; index < sortedDesktopPosts.length; index += cardsPerSlide) {
        const slide = document.createElement('div');
        const pageNumber = Math.floor(index / cardsPerSlide) + 1;
        slide.id = `Slide-${sliderIdBase}-${pageNumber}`;
        slide.className = 'slider-slide slider__slide--full-width';
        sortedDesktopPosts.slice(index, index + cardsPerSlide).forEach((post) => slide.appendChild(post));
        track.appendChild(slide);
    }

    desktopSlider.resetPages?.();
    desktopSlider.blogPaginationUpdate?.();
};

const sortBlogPanel = (panel, sortValue, sectionRoot) => {
    if (!panel || !sectionRoot) return;

    const desktopSlider = panel.querySelector('slider-component.desktop');
    const listData = getPanelPostList(panel);
    if (!desktopSlider || !listData) return;

    const { container, posts } = listData;
    if (posts.length < 2) return;

    posts.sort((postA, postB) =>
        compareBlogPosts(postA, postB, sortValue, getRelevantOrder(sectionRoot))
    );

    const fragment = document.createDocumentFragment();
    posts.forEach((post) => fragment.appendChild(post));
    container.appendChild(fragment);

    rebuildDesktopSlides(
        desktopSlider,
        posts,
        parseInt(desktopSlider.dataset.cardsPerSlide, 10) || 6
    );
    resetBlogSliders(panel);
};

const syncBlogSortVisibility = (blogTab) => {
    const sortEl = blogTab?.querySelector('[data-blog-sort]');
    if (!sortEl) return;

    const visiblePanel = blogTab.querySelector('.blog-sub-panel:not([hidden])');
    sortEl.hidden = visiblePanel?.dataset.hasPosts !== 'true';
};

const applySortToBlogTab = (sectionRoot, blogTab, sortValue) => {
    if (!sectionRoot || !blogTab) return;

    const visiblePanel = blogTab.querySelector('.blog-sub-panel:not([hidden])');
    if (!visiblePanel) return;

    if (sortValue === 'most-relevant' && !sectionRoot.dataset.relevantOrder) {
        resetBlogSliders(visiblePanel);
        return;
    }

    sortBlogPanel(visiblePanel, sortValue, sectionRoot);
};

const initBlogSort = (root = document) => {
    initOncePerSection(root, 'blogSortInit', (sectionRoot) => {
        sectionRoot.querySelectorAll('[data-blog-sort-select]').forEach((select) => {
            select.addEventListener('change', () => {
                const blogTab = select.closest('.blog-tab');
                if (!blogTab) return;

                blogTab.dataset.currentSort = select.value;
                applySortToBlogTab(sectionRoot, blogTab, select.value);
            });
        });
    });
};

// ---------------------------------------------------------------------------
// Tabs and sub-filters
// ---------------------------------------------------------------------------

const LEARNING_CENTER_CATEGORY_PARAM = 'category';
const LEARNING_CENTER_TOPIC_PARAM = 'topic';

const LEARNING_CENTER_TOPIC_ALIASES = {
    all: 'all',
    sample: 'sampling',
    sampling: 'sampling',
    sort: 'sort',
    test: 'testing',
    testing: 'testing',
    grade: 'grading',
    grading: 'grading',
    clean: 'clean',
    cleaning: 'clean',
};

const normalizeLearningCenterHandle = (value) => {
    if (!value) return null;
    return value.trim().toLowerCase().replace(/\s+/g, '-');
};

const normalizeLearningCenterTopic = (value) => {
    if (!value) return null;
    const normalized = normalizeLearningCenterHandle(value);
    return LEARNING_CENTER_TOPIC_ALIASES[normalized] || normalized;
};

const getLearningCenterUrlState = () => {
    const params = new URLSearchParams(window.location.search);

    return {
        category: normalizeLearningCenterHandle(params.get(LEARNING_CENTER_CATEGORY_PARAM)),
        topic: normalizeLearningCenterTopic(params.get(LEARNING_CENTER_TOPIC_PARAM)),
    };
};

const findMainTabIdByCategoryHandle = (sectionRoot, categoryHandle) => {
    if (!sectionRoot || !categoryHandle) return null;

    const toggle = sectionRoot.querySelector(
        `.collapsible-desktop-toggle[data-category-handle="${categoryHandle}"]`
    );

    return toggle?.dataset.selector || null;
};

const getCategoryHandleFromBlogTab = (blogTab) => blogTab?.dataset.categoryHandle || null;

const updateLearningCenterUrl = (categoryHandle, topicHandle) => {
    if (!categoryHandle) return;

    const url = new URL(window.location.href);
    url.searchParams.set(LEARNING_CENTER_CATEGORY_PARAM, categoryHandle);

    if (topicHandle) {
        url.searchParams.set(LEARNING_CENTER_TOPIC_PARAM, topicHandle);
    } else {
        url.searchParams.delete(LEARNING_CENTER_TOPIC_PARAM);
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl !== currentUrl) {
        window.history.replaceState(
            { learningCenter: { category: categoryHandle, topic: topicHandle } },
            '',
            nextUrl
        );
    }
};

const resolveInitialMainTabId = (sectionRoot, mainSelect) => {
    const { category } = getLearningCenterUrlState();
    const linkedTabId = findMainTabIdByCategoryHandle(sectionRoot, category);

    if (linkedTabId) return linkedTabId;

    return mainSelect?.value
        || sectionRoot.querySelector('.collapsible-desktop-toggle.is-open')?.dataset.selector
        || sectionRoot.querySelector('.blog-tab:not([hidden]) [data-blog-tab-title]')?.id
        || sectionRoot.querySelector('[data-blog-tab-title]')?.id
        || null;
};

let learningCenterPopStateBound = false;

const bindLearningCenterPopState = () => {
    if (learningCenterPopStateBound) return;
    learningCenterPopStateBound = true;

    window.addEventListener('popstate', () => {
        forEachSection(document, (sectionRoot) => {
            const mainSelect = sectionRoot.querySelector('[data-blog-main-tab-select]');
            const tabId = resolveInitialMainTabId(sectionRoot, mainSelect);
            const { topic } = getLearningCenterUrlState();

            if (tabId) {
                activateMainTab(sectionRoot, tabId, { isInitial: true, preferredTopic: topic });
            }
        });
    });
};

const syncMobileSubSelect = (blogTab, subTag) => {
    const subSelect = blogTab?.querySelector('[data-blog-sub-tab-select]');
    if (subSelect && subSelect.value !== subTag) {
        subSelect.value = subTag;
    }
};

const subPanelHasPosts = (panel) => panel?.dataset.hasPosts === 'true';

const getFirstAvailableSubTag = (blogTab) => {
    if (!blogTab) return null;

    const panelWithPosts = Array.from(blogTab.querySelectorAll('.blog-sub-panel'))
        .find((panel) => subPanelHasPosts(panel));

    return panelWithPosts?.dataset.subTag || null;
};

const resolveSubTagForBlogTab = (blogTab, preferredSubTag) => {
    if (!blogTab) return null;

    const preferredPanel = preferredSubTag
        ? blogTab.querySelector(`.blog-sub-panel[data-sub-tag="${preferredSubTag}"]`)
        : null;

    if (subPanelHasPosts(preferredPanel)) {
        return preferredSubTag;
    }

    return getFirstAvailableSubTag(blogTab);
};

const syncSubTabVisibility = (sectionRoot, blogTab) => {
    if (!sectionRoot || !blogTab) return;

    sectionRoot.querySelectorAll('.collapsible-sub-toggle').forEach((toggle) => {
        const panel = blogTab.querySelector(`.blog-sub-panel[data-sub-tag="${toggle.dataset.subTag}"]`);
        toggle.hidden = !subPanelHasPosts(panel);
    });

    blogTab.querySelectorAll('[data-blog-sub-tab-select] option').forEach((option) => {
        const panel = blogTab.querySelector(`.blog-sub-panel[data-sub-tag="${option.value}"]`);
        const hasPosts = subPanelHasPosts(panel);
        option.hidden = !hasPosts;
        option.disabled = !hasPosts;
    });
};

const updateDesktopLinks = (sectionRoot, id) => {
    if (!sectionRoot) return;

    sectionRoot.querySelectorAll('.collapsible-desktop-toggle').forEach((toggleEl) => {
        toggleEl.classList.toggle('is-open', toggleEl.dataset.selector === id);
    });
};

const updateSectionTitle = (sectionRoot, blogTab, subTag) => {
    if (!sectionRoot || !blogTab || !subTag) return;

    const titleEl = blogTab.querySelector('.blog__title');
    const mainTitleEl = blogTab.querySelector('[data-blog-tab-title]');
    if (!titleEl || !mainTitleEl) return;

    const mainTitle = mainTitleEl.textContent.trim();
    const subToggle = sectionRoot.querySelector(`.collapsible-sub-toggle[data-sub-tag="${subTag}"]`);
    const subTitle = subToggle?.querySelector('.collapsible-sub-title')?.textContent.trim() || '';

    if (mainTitle && subTitle) {
        titleEl.textContent = `${mainTitle} - ${subTitle}`;
    }
};

const updateSubToggleUnderline = (sectionRoot, subTag) => {
    const subTogglesContainer = sectionRoot?.querySelector('.collapsible-sub-toggles-container--desktop');
    if (!subTogglesContainer || !subTag) return;

    subTogglesContainer.style.setProperty('--active-sub-tab-anchor', `--sub-tab-${subTag}`);
};

const applySubFilter = (sectionRoot, subTag, scopeBlogTab, { isInitial = false } = {}) => {
    if (!sectionRoot || !subTag) return;

    sectionRoot.querySelectorAll('.collapsible-sub-toggle').forEach((toggle) => {
        toggle.classList.toggle('is-open', toggle.dataset.subTag === subTag);
    });

    updateSubToggleUnderline(sectionRoot, subTag);

    const blogTab = scopeBlogTab
        || sectionRoot.querySelector('.horizontal-tabs .blog-tab:not([hidden])');
    if (!blogTab) return;

    blogTab.querySelectorAll('.blog-sub-panel, .blog__description').forEach((panel) => {
        panel.hidden = panel.dataset.subTag !== subTag;
    });

    syncMobileSubSelect(blogTab, subTag);

    const visiblePanel = blogTab.querySelector(`.blog-sub-panel[data-sub-tag="${subTag}"]`);
    if (visiblePanel && !isInitial) resetBlogSliders(visiblePanel);

    updateSectionTitle(sectionRoot, blogTab, subTag);
    syncBlogSortVisibility(blogTab);

    if (!isInitial) {
        applySortToBlogTab(
            sectionRoot,
            blogTab,
            blogTab.dataset.currentSort || getDefaultSort(sectionRoot)
        );

        const categoryHandle = getCategoryHandleFromBlogTab(blogTab);
        if (categoryHandle) {
            updateLearningCenterUrl(categoryHandle, subTag);
        }
    }
};

const activateMainTab = (sectionRoot, tabId, { isInitial = false, preferredTopic = null } = {}) => {
    const tabLabel = document.getElementById(tabId);
    const collapsibleContainer = sectionRoot?.querySelector('.collapsible-content-container.one-rule');
    const blogTab = tabLabel?.closest('.blog-tab');
    if (!tabLabel || !collapsibleContainer || !blogTab) return;

    collapsibleContainer.querySelectorAll('.blog-tab').forEach((element) => {
        element.hidden = element !== blogTab;
    });

    updateDesktopLinks(sectionRoot, tabId);

    const mainSelect = sectionRoot.querySelector('[data-blog-main-tab-select]');
    if (mainSelect && mainSelect.value !== tabId) {
        mainSelect.value = tabId;
        syncCategorySelectTrigger(mainSelect);
    }

    syncSubTabVisibility(sectionRoot, blogTab);

    const subSelect = blogTab.querySelector('[data-blog-sub-tab-select]');
    const urlTopic = isInitial ? (preferredTopic ?? getLearningCenterUrlState().topic) : null;
    const preferredSubTag = urlTopic
        || subSelect?.value
        || blogTab.querySelector('.blog-sub-panel:not([hidden])')?.dataset.subTag
        || blogTab.querySelector('.blog-sub-panel')?.dataset.subTag;
    const subTag = resolveSubTagForBlogTab(blogTab, preferredSubTag);

    if (subTag) {
        syncMobileSubSelect(blogTab, subTag);
        applySubFilter(sectionRoot, subTag, blogTab, { isInitial });

        if (isInitial) {
            const categoryHandle = getCategoryHandleFromBlogTab(blogTab);
            if (categoryHandle) {
                updateLearningCenterUrl(categoryHandle, subTag);
            }
        }
    }
};

const syncCategorySelectTrigger = (select) => {
    const wrap = select?.closest('[data-blog-category-select]');
    if (wrap && typeof wrap.__updateCategorySelectTrigger === 'function') {
        wrap.__updateCategorySelectTrigger();
    }
};

const initBlogCategorySelect = (root = document) => {
    initOncePerSection(root, 'blogCategorySelectInit', (sectionRoot) => {
        if (!usesMobileDropdownPicker(sectionRoot)) return;

        sectionRoot.querySelectorAll('[data-blog-category-select]').forEach((wrap) => {
            const select = wrap.querySelector('[data-blog-main-tab-select]');
            const trigger = wrap.querySelector('[data-blog-category-select-trigger]');
            const list = wrap.querySelector('[data-blog-category-select-list]');
            const labelEl = wrap.querySelector('[data-blog-category-select-label]');
            const iconEl = wrap.querySelector('[data-blog-category-select-icon]');
            if (!select || !trigger || !list || !labelEl || !iconEl) return;

            const renderOptionIcon = (iconWrap, iconUrl) => {
                if (!iconUrl) {
                    iconWrap.innerHTML = '';
                    iconWrap.hidden = true;
                    return;
                }

                iconWrap.hidden = false;
                iconWrap.innerHTML = `<img src="${iconUrl}" alt="" width="28" height="28" loading="lazy">`;
            };

            const closeList = () => {
                list.hidden = true;
                trigger.setAttribute('aria-expanded', 'false');
            };

            const openList = () => {
                list.hidden = false;
                trigger.setAttribute('aria-expanded', 'true');
            };

            const updateTrigger = () => {
                const option = select.selectedOptions[0];
                if (!option) return;

                labelEl.textContent = option.textContent.trim();
                renderOptionIcon(iconEl, option.dataset.iconUrl);

                list.querySelectorAll('[role="option"]').forEach((item) => {
                    item.setAttribute('aria-selected', item.dataset.value === option.value ? 'true' : 'false');
                });
            };

            list.innerHTML = '';
            Array.from(select.options).forEach((option) => {
                const item = document.createElement('li');
                item.setAttribute('role', 'option');
                item.dataset.value = option.value;
                item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'blog__category-select-option';

                if (option.dataset.iconUrl) {
                    const optionIcon = document.createElement('span');
                    optionIcon.className = 'blog__sort-select-icon';
                    optionIcon.setAttribute('aria-hidden', 'true');
                    optionIcon.innerHTML = `<img src="${option.dataset.iconUrl}" alt="" width="28" height="28" loading="lazy">`;
                    button.appendChild(optionIcon);
                }

                const optionLabel = document.createElement('span');
                optionLabel.className = 'blog__category-select-option__label';
                optionLabel.textContent = option.textContent.trim();
                button.appendChild(optionLabel);

                button.addEventListener('click', () => {
                    if (select.value === option.value) {
                        closeList();
                        return;
                    }

                    select.value = option.value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                    updateTrigger();
                    closeList();
                });

                item.appendChild(button);
                list.appendChild(item);
            });

            trigger.addEventListener('click', () => {
                if (list.hidden) openList();
                else closeList();
            });

            trigger.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') closeList();
            });

            document.addEventListener('click', (event) => {
                if (!wrap.contains(event.target)) closeList();
            });

            wrap.__updateCategorySelectTrigger = updateTrigger;
            updateTrigger();
        });
    });
};

const initBlogMobileDropdowns = (root = document) => {
    initOncePerSection(root, 'blogMobileDropdownsInit', (sectionRoot) => {
        const mainSelect = sectionRoot.querySelector('[data-blog-main-tab-select]');

        mainSelect?.addEventListener('change', () => {
            activateMainTab(sectionRoot, mainSelect.value);
        });

        sectionRoot.querySelectorAll('[data-blog-sub-tab-select]').forEach((select) => {
            select.addEventListener('change', () => {
                const blogTab = select.closest('.blog-tab');
                if (blogTab) applySubFilter(sectionRoot, select.value, blogTab);
            });
        });

        bindLearningCenterPopState();

        const urlState = getLearningCenterUrlState();
        const defaultTabId = resolveInitialMainTabId(sectionRoot, mainSelect);
        const { topic } = urlState;

        if (defaultTabId) {
            activateMainTab(sectionRoot, defaultTabId, { isInitial: true, preferredTopic: topic });

            if (urlState.category) {
                requestAnimationFrame(() => {
                    sectionRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
            }
        }
    });
};

// ---------------------------------------------------------------------------
// Featured slider announcements
// ---------------------------------------------------------------------------

const initFeaturedBlogSliderAnnouncements = (root = document) => {
    forEachSection(root, (sectionRoot) => {
        const sectionHeading = sectionRoot.dataset.sectionHeading || '';
        const announcementTemplate = sectionHeading
            ? `Now showing slide __CURRENT__ of __TOTAL__ for ${sectionHeading}`
            : 'Now showing slide __CURRENT__ of __TOTAL__';

        sectionRoot.querySelectorAll('slider-component').forEach((slider) => {
            if (slider.dataset.sliderA11yInit) return;

            const prevButton = slider.querySelector('button[name="previous"]');
            const nextButton = slider.querySelector('button[name="next"]');
            const liveRegion = slider.querySelector('.slider-live-region');
            if (!prevButton || !nextButton || !liveRegion) return;

            slider.dataset.sliderA11yInit = 'true';

            let announceFromButton = false;

            const announceSlide = () => {
                const current = slider.querySelector('.slider-counter--current')?.textContent.trim() || '1';
                const total = slider.querySelector('.slider-counter--total')?.textContent.trim() || '1';

                liveRegion.textContent = announcementTemplate
                    .replace('__CURRENT__', current)
                    .replace('__TOTAL__', total);
            };

            slider.addEventListener('slideChanged', () => {
                if (!announceFromButton) return;
                announceFromButton = false;
                announceSlide();
            });

            [prevButton, nextButton].forEach((button) => {
                button.addEventListener('click', () => {
                    announceFromButton = true;
                });
            });
        });
    });
};

// ---------------------------------------------------------------------------
// Article video modals
// ---------------------------------------------------------------------------

const createVideoIframe = (src, className, title) => {
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.className = className;
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    if (title) iframe.setAttribute('title', title);
    return iframe;
};

const PLAY_ICON_SVG = `
    <svg
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    class="icon icon-play"
    fill="none"
    viewBox="0 0 10 14"
    >
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.48177 0.814643C0.81532 0.448245 0 0.930414 0 1.69094V12.2081C0 12.991 0.858787 13.4702 1.52503 13.0592L10.5398 7.49813C11.1918 7.09588 11.1679 6.13985 10.4965 5.77075L1.48177 0.814643Z" fill="currentColor"/>
    </svg>
`;

const getVideoThumbnailUrl = (videoType, videoId, thumbnailFromCard) => {
    if (thumbnailFromCard) return thumbnailFromCard;
    if (videoType === 'youtube' && videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
    return '';
};

const buildArticleVideoEmbed = (videoType, videoId, videoSrc, title, { autoplay = false } = {}) => {
    if (videoType === 'youtube' && videoId) {
        const params = new URLSearchParams({ enablejsapi: '1' });
        if (autoplay) params.set('autoplay', '1');
        return createVideoIframe(
            `https://www.youtube.com/embed/${videoId}?${params.toString()}`,
            'js-youtube',
            title
        );
    }

    if (videoType === 'vimeo' && videoId) {
        const params = new URLSearchParams();
        if (autoplay) params.set('autoplay', '1');
        const query = params.toString();
        return createVideoIframe(
            `https://player.vimeo.com/video/${videoId}${query ? `?${query}` : ''}`,
            'js-vimeo',
            title
        );
    }

    if (videoType === 'html5' && videoSrc) {
        const video = document.createElement('video');
        video.src = videoSrc;
        video.controls = true;
        video.playsInline = true;
        if (autoplay) video.autoplay = true;
        if (title) video.setAttribute('title', title);
        return video;
    }

    return null;
};

const buildArticleVideoPoster = (videoType, videoId, videoSrc, title, thumbnailUrl) => {
    const poster = document.createElement('button');
    poster.type = 'button';
    poster.className = 'article-video-modal__poster';
    poster.dataset.videoType = videoType;
    if (videoId) poster.dataset.videoId = videoId;
    if (videoSrc) poster.dataset.videoSrc = videoSrc;
    if (title) poster.dataset.videoTitle = title;
    poster.setAttribute('aria-label', title ? `Play video: ${title}` : 'Play video');

    const resolvedThumbnail = getVideoThumbnailUrl(videoType, videoId, thumbnailUrl);
    if (resolvedThumbnail) {
        const image = document.createElement('img');
        image.className = 'article-video-modal__poster-image';
        image.src = resolvedThumbnail;
        image.alt = '';
        poster.appendChild(image);
    }

    const playButton = document.createElement('span');
    playButton.className = 'article-video-modal__poster-button';
    playButton.setAttribute('aria-hidden', 'true');
    playButton.innerHTML = PLAY_ICON_SVG;
    poster.appendChild(playButton);

    return poster;
};

const playArticleVideoFromPoster = (poster, player) => {
    const embed = buildArticleVideoEmbed(
        poster.dataset.videoType,
        poster.dataset.videoId,
        poster.dataset.videoSrc,
        poster.dataset.videoTitle,
        { autoplay: true }
    );
    if (!embed) return;

    player.replaceChildren(embed);
    if (typeof embed.focus === 'function') embed.focus();
};

const clearArticleVideoPlayers = () => {
    document.querySelectorAll('.article-video-modal__player').forEach((player) => {
        player.innerHTML = '';
    });
};

const getArticleVideoModal = (sectionRoot) => {
    const sectionId = sectionRoot?.dataset.customBlogSection;
    if (sectionId) {
        return document.getElementById(`ArticleVideoModal-${sectionId}`);
    }

    return document.querySelector('[data-article-video-modal]');
};

const openArticleVideoModal = (card, sectionRoot) => {
    const modal = getArticleVideoModal(sectionRoot);
    if (!modal || !card.dataset.videoType) return;

    const player = modal.querySelector('.article-video-modal__player');
    if (!player) return;

    const poster = buildArticleVideoPoster(
        card.dataset.videoType,
        card.dataset.videoId,
        card.dataset.videoSrc,
        card.dataset.videoTitle,
        card.dataset.videoThumbnail
    );

    player.replaceChildren(poster);

    const dialog = modal.querySelector('[role="dialog"]');
    if (dialog && card.dataset.videoTitle) {
        dialog.setAttribute('aria-label', card.dataset.videoTitle);
    }

    modal.show(card);
};

const bindArticleVideoModalCleanup = () => {
    if (articleVideoModalCleanupBound) return;
    articleVideoModalCleanupBound = true;
    document.body.addEventListener('modalClosed', clearArticleVideoPlayers);
};

// ---------------------------------------------------------------------------
// Event delegation
// ---------------------------------------------------------------------------

const onDocumentClick = (event) => {
    const { target } = event;

    const videoPoster = target.closest('.article-video-modal__poster');
    if (videoPoster) {
        event.preventDefault();
        const player = videoPoster.closest('.article-video-modal__player');
        if (player) playArticleVideoFromPoster(videoPoster, player);
        return;
    }

    const videoCard = target.closest('.article-card-wrapper--video');
    if (videoCard && !target.closest('.view-article')) {
        const sectionRoot = getCustomBlogSection(videoCard);
        if (!sectionRoot) return;

        event.preventDefault();

        if (videoCard.dataset.videoType) {
            openArticleVideoModal(videoCard, sectionRoot);
            return;
        }

        if (videoCard.dataset.articleUrl) {
            window.location.href = `${videoCard.dataset.articleUrl}#article-video`;
        }
        return;
    }

    const subToggle = target.closest('.collapsible-sub-toggle');
    if (subToggle) {
        event.preventDefault();
        if (subToggle.hidden) return;

        const sectionRoot = getCustomBlogSection(subToggle);
        if (sectionRoot) {
            const blogTab = sectionRoot.querySelector('.horizontal-tabs .blog-tab:not([hidden])');
            applySubFilter(sectionRoot, subToggle.dataset.subTag, blogTab);
        }
        return;
    }

    const desktopToggle = target.closest('.collapsible-desktop-toggle');
    if (desktopToggle) {
        const sectionRoot = getCustomBlogSection(desktopToggle);
        if (
            sectionRoot
            && (!isTabletDownBlogLayout() || !usesMobileDropdownPicker(sectionRoot))
        ) {
            event.preventDefault();
            activateMainTab(sectionRoot, desktopToggle.dataset.selector);
        }
    }
};

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

const SECTION_FEATURE_INITS = [
    initBlogSliderPagination,
    initBlogSort,
    initBlogCategorySelect,
    initBlogMobileDropdowns,
    initFeaturedBlogSliderAnnouncements,
];

const initCustomBlog = (root = document) => {
    SECTION_FEATURE_INITS.forEach((initFn) => initFn(root));
    bindArticleVideoModalCleanup();
};

(function () {
    document.addEventListener('click', onDocumentClick);
    initCustomBlog();

    document.addEventListener('shopify:section:load', (event) => {
        initCustomBlog(event.target);
    });
})();
